import { useState, useEffect, useCallback } from "react";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { useParams, useNavigate, Link } from "react-router-dom";

const SENTIMENT_COLORS = {
    positive: "#22c55e",
    neutral:  "#94a3b8",
    negative: "#ef4444",
    mixed:    "#f59e0b",
};

const INTENT_LABELS = {
    buy: "Buy", get_info: "Get Info", complain: "Complaint",
    apply: "Apply", other: "Other", unclear: "Unclear"
};

function SentimentBar({ label, count, total, color }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="flex items-center gap-3 text-sm">
            <span className="w-20 text-right text-gray-600 dark:text-gray-400 font-medium capitalize">{label}</span>
            <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-5 overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                />
            </div>
            <span className="w-12 text-gray-700 dark:text-gray-300 font-semibold">{count} <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">({pct}%)</span></span>
        </div>
    );
}

function ThemeBar({ theme, count, maxCount }) {
    const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
    return (
        <div className="flex items-center gap-3 text-sm">
            <span className="w-36 truncate text-gray-700 dark:text-gray-300 font-medium" title={theme}>{theme}</span>
            <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded h-4 overflow-hidden">
                <div
                    className="h-full rounded bg-indigo-500 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className="w-6 text-gray-500 dark:text-gray-400 text-xs">{count}</span>
        </div>
    );
}

function LeadScoreHistogram({ distribution }) {
    const buckets = Object.entries(distribution);
    const maxVal = Math.max(...buckets.map(([, v]) => v), 1);
    const colors = ["bg-red-400", "bg-orange-400", "bg-yellow-400", "bg-lime-400", "bg-green-500"];

    return (
        <div className="flex items-end gap-2 h-32">
            {buckets.map(([label, count], i) => {
                const pct = Math.round((count / maxVal) * 100);
                return (
                    <div key={label} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{count}</span>
                        <div className="w-full flex items-end justify-center">
                            <div
                                className={`w-full rounded-t transition-all duration-500 ${colors[i]}`}
                                style={{ height: `${Math.max(pct * 0.9, 4)}px` }}
                            />
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 text-center leading-tight">{label}</span>
                    </div>
                );
            })}
        </div>
    );
}

function IntentDonut({ intentBreakdown }) {
    const entries = Object.entries(intentBreakdown);
    const total = entries.reduce((s, [, v]) => s + v, 0);
    if (total === 0) return <p className="text-gray-400 dark:text-gray-500 text-sm">No intent data yet</p>;

    const palette = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6"];
    let cumulativePct = 0;
    const segments = entries.map(([label, count], i) => {
        const pct = (count / total) * 100;
        const start = cumulativePct;
        cumulativePct += pct;
        return { label, count, pct, start, color: palette[i % palette.length] };
    });

    // Build SVG conic-gradient fallback with circle segments
    const cx = 60, cy = 60, r = 50;
    const polarToXY = (deg) => {
        const rad = ((deg - 90) * Math.PI) / 180;
        return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };

    const paths = segments.map(({ pct, start, color }) => {
        if (pct === 0) return null;
        const startDeg = (start / 100) * 360;
        const endDeg = ((start + pct) / 100) * 360;
        const startPt = polarToXY(startDeg);
        const endPt = polarToXY(endDeg);
        const largeArc = pct > 50 ? 1 : 0;
        return `M ${cx} ${cy} L ${startPt.x} ${startPt.y} A ${r} ${r} 0 ${largeArc} 1 ${endPt.x} ${endPt.y} Z`;
    });

    return (
        <div className="flex flex-wrap items-center gap-6">
            <svg viewBox="0 0 120 120" className="w-32 h-32">
                {paths.map((d, i) => d && <path key={i} d={d} fill={segments[i].color} />)}
                <circle cx={cx} cy={cy} r={30} className="fill-white dark:fill-[#181d33]" />
            </svg>
            <div className="space-y-1.5">
                {segments.map(({ label, count, pct, color }) => (
                    <div key={label} className="flex items-center gap-2 text-sm">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-gray-700 dark:text-gray-300 capitalize">{INTENT_LABELS[label] || label}</span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">({count}, {Math.round(pct)}%)</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SentimentTimeline({ data }) {
    if (!data || data.length === 0) return <p className="text-gray-400 dark:text-gray-500 text-sm">No timeline data yet</p>;
    const last14 = data.slice(-14);
    const maxPerDay = Math.max(...last14.map(d => (d.positive || 0) + (d.neutral || 0) + (d.negative || 0) + (d.mixed || 0)), 1);

    return (
        <div className="overflow-x-auto">
            <div className="flex items-end gap-1 h-24 min-w-max">
                {last14.map((d) => {
                    const tot = (d.positive || 0) + (d.neutral || 0) + (d.negative || 0) + (d.mixed || 0);
                    const h = (tot / maxPerDay) * 96;
                    const segments = [
                        { key: "positive", pct: tot ? ((d.positive || 0) / tot * 100) : 0, color: SENTIMENT_COLORS.positive },
                        { key: "neutral",  pct: tot ? ((d.neutral || 0)  / tot * 100) : 0, color: SENTIMENT_COLORS.neutral },
                        { key: "mixed",    pct: tot ? ((d.mixed || 0)    / tot * 100) : 0, color: SENTIMENT_COLORS.mixed },
                        { key: "negative", pct: tot ? ((d.negative || 0) / tot * 100) : 0, color: SENTIMENT_COLORS.negative },
                    ];
                    return (
                        <div key={d.date} className="flex flex-col items-center gap-1" title={`${d.date}: ${tot} responses`}>
                            <div className="w-7 flex flex-col-reverse rounded-t overflow-hidden" style={{ height: `${Math.max(h, 4)}px` }}>
                                {segments.map(s => s.pct > 0 && (
                                    <div key={s.key} style={{ height: `${s.pct}%`, backgroundColor: s.color }} />
                                ))}
                            </div>
                            <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 rotate-[-45deg] origin-top-left whitespace-nowrap" style={{ fontSize: "9px" }}>
                                {d.date.slice(5)}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="flex gap-3 mt-4">
                {Object.entries(SENTIMENT_COLORS).map(([k, c]) => (
                    <span key={k} className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 capitalize">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />{k}
                    </span>
                ))}
            </div>
        </div>
    );
}

function FormInsights() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { getForm, getFormInsights, loading, error } = useFormBuilder();

    const [form, setForm] = useState(null);
    const [insights, setInsights] = useState(null);

    const loadData = useCallback(async () => {
        const [formData, insightsData] = await Promise.all([
            getForm(formId),
            getFormInsights(formId)
        ]);
        if (formData) setForm(formData);
        if (insightsData) setInsights(insightsData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId]);

    useEffect(() => { loadData(); }, [loadData]);

    const sentimentTotal = insights
        ? Object.values(insights.sentimentDistribution || {}).reduce((a, b) => a + b, 0)
        : 0;

    return (
        <div className="min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-8 font-Rajdhani">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-center mt-5 mb-6">
                <div>
                    <button
                        className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-1"
                        onClick={() => navigate(`/form-builder/${formId}/responses`)}
                    >
                        &larr; Back to Responses
                    </button>
                    <h1 className="text-2xl font-bold">Insights{form ? `: ${form.title}` : ""}</h1>
                    {insights && !insights.empty && (
                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                            Based on {insights.totalAnalyzed} analyzed responses
                        </p>
                    )}
                </div>
                <Link
                    to={`/form-builder/${formId}/responses`}
                    className="px-4 py-2 text-sm border-[1px] dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2c2f44] font-medium no-underline dark:text-white"
                >
                    View Responses
                </Link>
            </div>

            {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1,2,3,4].map(i => (
                        <div key={i} className="border-[1px] dark:border-gray-700 rounded-xl p-6 animate-pulse">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
                            <div className="space-y-2">
                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded" />
                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && error && (
                <div className="border-[1px] dark:border-gray-700 rounded-xl p-8 text-center bg-[#ffffff] dark:bg-[#23263a]">
                    <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">{error}</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                        {error.includes("Pro") ? "Upgrade to Pro to unlock the Insights tab." : ""}
                    </p>
                </div>
            )}

            {!loading && insights?.empty && (
                <div className="border-[1px] dark:border-gray-700 rounded-xl p-12 text-center bg-[#ffffff] dark:bg-[#23263a]">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                        <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                    </div>
                    <p className="text-xl text-gray-500 dark:text-gray-400 mb-2">No analyzed responses yet</p>
                    <p className="text-gray-400 dark:text-gray-500 font-medium">Analysis runs automatically after each submission.</p>
                </div>
            )}

            {!loading && insights && !insights.empty && (
                <div className="space-y-4">
                    {/* Top row: stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-4 bg-[#ffffff] dark:bg-[#23263a]">
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Analyzed</p>
                            <p className="text-3xl font-bold mt-1 font-display">{insights.totalAnalyzed}</p>
                        </div>
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-4 bg-[#ffffff] dark:bg-[#23263a]">
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Avg Lead Score</p>
                            <p className="text-3xl font-bold mt-1 font-display text-indigo-600 dark:text-indigo-400">{insights.avgLeadScore}</p>
                        </div>
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-4 bg-[#ffffff] dark:bg-[#23263a]">
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Hot Leads</p>
                            <p className="text-3xl font-bold mt-1 font-display text-green-600 dark:text-green-400">
                                {insights.leadScoreDistribution?.["81-100"] || 0}
                            </p>
                        </div>
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-4 bg-[#ffffff] dark:bg-[#23263a]">
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">AI Cost</p>
                            <p className="text-3xl font-bold mt-1 font-display text-gray-700 dark:text-gray-300">
                                ${((insights.totalCostCents || 0) / 100).toFixed(2)}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Sentiment distribution */}
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-5 bg-[#ffffff] dark:bg-[#23263a]">
                            <h3 className="text-base font-bold mb-4">Sentiment Breakdown</h3>
                            <div className="space-y-3">
                                {Object.entries(insights.sentimentDistribution || {}).map(([s, count]) => (
                                    <SentimentBar
                                        key={s}
                                        label={s}
                                        count={count}
                                        total={sentimentTotal}
                                        color={SENTIMENT_COLORS[s] || "#94a3b8"}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Intent breakdown */}
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-5 bg-[#ffffff] dark:bg-[#23263a]">
                            <h3 className="text-base font-bold mb-4">Intent Breakdown</h3>
                            {Object.keys(insights.intentBreakdown || {}).length > 0 ? (
                                <IntentDonut intentBreakdown={insights.intentBreakdown} />
                            ) : (
                                <p className="text-sm text-gray-400 dark:text-gray-500">No intent data (requires Pro plan)</p>
                            )}
                        </div>

                        {/* Top themes */}
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-5 bg-[#ffffff] dark:bg-[#23263a]">
                            <h3 className="text-base font-bold mb-4">Top Themes</h3>
                            {insights.topThemes?.length > 0 ? (
                                <div className="space-y-2">
                                    {insights.topThemes.map(({ theme, count }) => (
                                        <ThemeBar
                                            key={theme}
                                            theme={theme}
                                            count={count}
                                            maxCount={insights.topThemes[0].count}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 dark:text-gray-500">No theme data (requires Pro plan)</p>
                            )}
                        </div>

                        {/* Lead score distribution */}
                        <div className="border-[1px] dark:border-gray-700 rounded-xl p-5 bg-[#ffffff] dark:bg-[#23263a]">
                            <h3 className="text-base font-bold mb-4">Lead Score Distribution</h3>
                            {insights.leadScoreDistribution ? (
                                <LeadScoreHistogram distribution={insights.leadScoreDistribution} />
                            ) : (
                                <p className="text-sm text-gray-400 dark:text-gray-500">No lead score data (requires Pro plan)</p>
                            )}
                        </div>
                    </div>

                    {/* Sentiment over time */}
                    <div className="border-[1px] dark:border-gray-700 rounded-xl p-5 bg-[#ffffff] dark:bg-[#23263a]">
                        <h3 className="text-base font-bold mb-4">Sentiment Over Time (last 14 days)</h3>
                        <SentimentTimeline data={insights.sentimentOverTime} />
                    </div>

                    {/* Top quotes */}
                    {(insights.topPositiveQuotes?.length > 0 || insights.topNegativeQuotes?.length > 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {insights.topPositiveQuotes?.length > 0 && (
                                <div className="border-[1px] dark:border-gray-700 rounded-xl p-5 bg-[#ffffff] dark:bg-[#23263a]">
                                    <h3 className="text-base font-bold mb-3 text-green-600 dark:text-green-400">Top Positive</h3>
                                    <div className="space-y-3">
                                        {insights.topPositiveQuotes.map((q, i) => (
                                            <blockquote key={i} className="border-l-4 border-green-400 pl-3 text-sm text-gray-600 dark:text-gray-400 italic">
                                                {q}
                                            </blockquote>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {insights.topNegativeQuotes?.length > 0 && (
                                <div className="border-[1px] dark:border-gray-700 rounded-xl p-5 bg-[#ffffff] dark:bg-[#23263a]">
                                    <h3 className="text-base font-bold mb-3 text-red-500 dark:text-red-400">Top Negative</h3>
                                    <div className="space-y-3">
                                        {insights.topNegativeQuotes.map((q, i) => (
                                            <blockquote key={i} className="border-l-4 border-red-400 pl-3 text-sm text-gray-600 dark:text-gray-400 italic">
                                                {q}
                                            </blockquote>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default FormInsights;
